import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    if (this.refs.details) {
      this.refs.details.addEventListener('toggle', () => {
        if (this.refs.details.hasAttribute('open')) {
          document.body.classList.add('megamenu-open');
        } else {
          if (!document.querySelector('megamenu-panel.megamenu-panel--open, header-drawer details[open]')) {
            document.body.classList.remove('megamenu-open');
          }
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details ? this.refs.details.classList.contains('menu-open') : false;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {string | Event} [targetOrEvent]
   * @param {Event} [event]
   */
  toggle(targetOrEvent, event) {
    const evt = targetOrEvent instanceof Event ? targetOrEvent : event;
    if (evt && typeof evt.preventDefault === 'function') {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const target = typeof targetOrEvent === 'string' ? targetOrEvent : undefined;
    return this.isOpen ? this.close(evt) : this.open(target, evt);
  }

  lastOpenedTime = 0;

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    this.lastOpenedTime = Date.now();
    const evt = target instanceof Event ? target : event;
    const targetStr = typeof target === 'string' ? target : undefined;
    if (evt && typeof evt.preventDefault === 'function') {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const details = this.#getDetailsElement(evt);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.querySelectorAll('details[open]').forEach((d) => {
      if (d !== details) reset(d);
    });

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      document.body.classList.add('megamenu-open');
      this.updateActiveDetailsState();

      if (targetStr) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Update active class (menu-open-latest) on the latest open submenu details element.
   */
  updateActiveDetailsState() {
    const allDetails = this.querySelectorAll('details');
    allDetails.forEach((d) => d.classList.remove('menu-open-latest'));

    const openDetails = Array.from(this.querySelectorAll('details.menu-open')).filter((d) => d !== this.refs.details);
    if (openDetails.length > 0) {
      const latestDetails = openDetails[openDetails.length - 1];
      latestDetails.classList.add('menu-open-latest');
    }
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    event?.stopPropagation?.();
    if (Date.now() - (this.lastOpenedTime || 0) < 300) {
      return;
    }
    const openDetails = Array.from(this.querySelectorAll('details.menu-open')).filter((d) => d !== this.refs.details);
    const latestDetails = openDetails.length > 0
      ? openDetails[openDetails.length - 1]
      : (event?.target instanceof Element ? event.target.closest('details') : null);

    if (latestDetails && latestDetails !== this.refs.details) {
      reset(latestDetails);
      this.updateActiveDetailsState();
    } else {
      this.close(event);
    }
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
      event.stopPropagation();
    }
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.classList.remove('menu-open', 'menu-open-latest');
    summary.setAttribute('aria-expanded', 'false');

    details.querySelectorAll('details').forEach((child) => {
      child.classList.remove('menu-open', 'menu-open-latest');
      child.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    this.updateActiveDetailsState();

    const parentSubmenuDetails = details.parentElement?.closest('details');
    const isParentSubmenuOpen =
      parentSubmenuDetails &&
      parentSubmenuDetails !== this.refs.details &&
      parentSubmenuDetails.hasAttribute('open');

    if (!isParentSubmenuOpen) {
      this.refs.menuDrawer?.classList.remove('menu-drawer--has-submenu-opened');
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    let finalized = false;
    const finalizeClose = () => {
      if (finalized) return;
      finalized = true;

      if (!details.classList.contains('menu-open')) {
        reset(details);
      }

      if (details === this.refs.details) {
        if (!document.querySelector('megamenu-panel.megamenu-panel--open, header-drawer details.menu-open')) {
          document.body.classList.remove('megamenu-open');
        }
        removeTrapFocus();
      } else if (isParentSubmenuOpen) {
        trapFocus(parentSubmenuDetails);
      } else {
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(drawer || details, finalizeClose, { subtree: false });
    setTimeout(finalizeClose, 350);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'menu-open-latest');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  element.querySelectorAll('details').forEach((child) => {
    child.classList.remove('menu-open', 'menu-open-latest');
    child.removeAttribute('open');
    child.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });
}
